import { useState } from "react";
import { Flame, Truck, Boxes, Layers, Factory, ChevronsUpDown, Check } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePabrik, GABUNGAN } from "@/context/PabrikContext";

export const JENIS = {
  tungku: { label: "Arang Kayu Tungku", icon: Flame, color: "#f59e0b" },
  reseller: { label: "Reseller Arang", icon: Truck, color: "#38bdf8" },
  briket: { label: "Briket", icon: Boxes, color: "#a78bfa" },
  [GABUNGAN]: { label: "Laporan total", icon: Layers, color: "#34d399" },
};
export const jenisOf = (j) => JENIS[j] || { label: j, icon: Factory, color: "#94a3b8" };

const MENU_JENIS = { "/produksi/kayu": ["tungku"], "/produksi/bakar": ["tungku"], "/produksi/pembelian": ["reseller"], "/produksi/bahan": ["briket"], "/produksi/briket": ["briket"], "/produksi/transfer": ["tungku", "reseller"] };
export const pathAllowed = (path, jenis) => !MENU_JENIS[path] || MENU_JENIS[path].includes(jenis);
const shortName = (n = "") => n.replace(/^Pabrik\s+/i, "");

const Row = ({ p, active, onPick }) => {
  const J = jenisOf(p.jenis);
  return (
    <button onClick={() => onPick(p)} data-testid={`pabrik-opt-${p.id}`}
      className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-left transition-colors duration-150 ${active ? "bg-slate-100" : "hover:bg-slate-50"}`}>
      <span className="h-7 w-7 rounded-md flex items-center justify-center shrink-0" style={{ background: `${J.color}1f` }}><J.icon size={14} style={{ color: J.color }}/></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] text-slate-800 truncate">{shortName(p.nama)}</span>
        <span className="block text-[11px] text-slate-400 truncate">{J.label}</span>
      </span>
      {active && <Check size={14} className="text-slate-700 shrink-0"/>}
    </button>
  );
};

/** Pemilih pabrik ringkas (dropdown). variant: "dark" (sidebar) | "light" (header mobile). */
export function PabrikSwitcher({ variant = "dark", onDone }) {
  const { enabled, items, current, canGabungan, switchTo } = usePabrik();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  if (!enabled || !current) return null;
  const J = jenisOf(current.jenis);
  const multi = items.length > 1 || canGabungan;
  const pick = (p) => {
    setOpen(false);
    if (p.id === current.id) return;
    switchTo(p.id);
    if (!pathAllowed(loc.pathname, p.jenis) || (p.id === GABUNGAN && loc.pathname === "/produksi/kategori")) nav("/dashboard");
    onDone?.();
  };
  const dark = variant === "dark";
  const trigger = (
    <button disabled={!multi} data-testid={dark ? "pabrik-switcher" : "pabrik-switcher-m"}
      className={dark
        ? "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition-colors duration-150 text-left disabled:cursor-default"
        : "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-slate-200 bg-white text-left max-w-[60vw] disabled:cursor-default"}>
      <span className={`${dark ? "h-7 w-7 rounded-md" : "h-5 w-5 rounded-full"} flex items-center justify-center shrink-0`} style={{ background: `${J.color}26` }}>
        <J.icon size={dark ? 14 : 12} style={{ color: J.color }}/>
      </span>
      <span className="min-w-0 flex-1">
        {dark && <span className="block text-[10px] uppercase tracking-wider text-slate-500 leading-none mb-0.5">Pabrik</span>}
        <span className={`block truncate ${dark ? "text-[13px] text-slate-100" : "text-xs font-medium text-slate-800"}`} data-testid="pabrik-current-name">{shortName(current.nama)}</span>
      </span>
      {multi && <ChevronsUpDown size={dark ? 14 : 12} className="text-slate-400 shrink-0"/>}
    </button>
  );
  if (!multi) return trigger;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-64 p-1.5 rounded-lg" data-testid="pabrik-menu">
        <div className="px-2.5 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-slate-400">Pilih pabrik</div>
        {items.map((p) => <Row key={p.id} p={p} active={p.id === current.id} onPick={pick}/>)}
        {canGabungan && <>
          <div className="my-1 border-t border-slate-100"/>
          <Row p={{ id: GABUNGAN, nama: "Semua Pabrik (Gabungan)", jenis: GABUNGAN }} active={current.id === GABUNGAN} onPick={pick}/>
        </>}
      </PopoverContent>
    </Popover>
  );
}

/** Info tipis hanya saat mode Gabungan. */
export function PabrikBanner() {
  const { enabled, current } = usePabrik();
  if (!enabled || current?.id !== GABUNGAN) return null;
  return (
    <div className="px-4 md:px-8 py-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2 text-[12px] text-emerald-800" data-testid="pabrik-banner">
      <Layers size={14}/><span className="font-medium" data-testid="pabrik-banner-nama">Semua Pabrik (Gabungan)</span>
      <span className="text-emerald-700/80 hidden sm:inline">· hanya lihat, transaksi antar-pabrik tidak dihitung</span>
    </div>
  );
}
